import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { IVector2 } from '@seddi/math-v2';
import type { SceneDesc } from '@seddi/ui-three-viewer';
import type { MockupLogo } from '../types/mockupLogos';

// full scene payload out of Redux

let sceneStateInternal: SceneDesc | null = null;

export const getScene = () => {
  return sceneStateInternal;
};

export const setScene = (scene: SceneDesc | null) => {
  sceneStateInternal = scene;
};

const ResolutionDef: IVector2 = { x: 1024, y: 1024 };

// scene state

export type TAppState = {
  resolution: IVector2;
  sceneIsLoading: boolean;
  /** URL of the style preview image shown while the 3D scene is loading. */
  stylePreviewUrl: string | null;
  /** Shown in viewer when no styles found (e.g. StyleNotFoundError). */
  styleNotFoundMessage: string | null;
  /** Current style type (e.g. "headwear") for camera framing. */
  styleType: string | null;
  // simplified scene state propagation
  sceneVersion: number;
  /** Logos from mockup (mockup_id mode) to display in the viewer. */
  mockupLogos: MockupLogo[];
  mockupHideTearAwayLabel: boolean;
};

const initialState: TAppState = {
  resolution: { ...ResolutionDef },
  sceneIsLoading: false,
  stylePreviewUrl: null,
  styleNotFoundMessage: null,
  styleType: null,
  sceneVersion: 0,
  mockupLogos: [],
  mockupHideTearAwayLabel: false,
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    clear: state => {
      state.resolution = { ...ResolutionDef };
      state.sceneIsLoading = false;
      state.stylePreviewUrl = null;
      state.styleNotFoundMessage = null;
      state.styleType = null;
      state.sceneVersion = 0;
      state.mockupLogos = [];
      state.mockupHideTearAwayLabel = false;
    },
    setResolution: (state, action: PayloadAction<IVector2>) => {
      state.resolution = action.payload;
    },
    setSceneIsLoading: (state, action: PayloadAction<boolean>) => {
      state.sceneIsLoading = action.payload;
    },
    setStylePreviewUrl: (state, action: PayloadAction<string | null>) => {
      state.stylePreviewUrl = action.payload;
    },
    setStyleNotFoundMessage: (state, action: PayloadAction<string | null>) => {
      state.styleNotFoundMessage = action.payload;
    },
    setStyleType: (state, action: PayloadAction<string | null>) => {
      state.styleType = action.payload;
    },
    updateScene: state => {
      state.sceneVersion = state.sceneVersion + 1;
    },
    setMockupLogos: (
      state,
      action: PayloadAction<{ logos: MockupLogo[]; hideTearAwayLabel: boolean }>,
    ) => {
      state.mockupLogos = action.payload.logos;
      state.mockupHideTearAwayLabel = action.payload.hideTearAwayLabel;
    },
    clearMockupLogos: state => {
      state.mockupLogos = [];
      state.mockupHideTearAwayLabel = false;
    },
  },
});

export const {
  clear,
  setResolution,
  setSceneIsLoading,
  setStylePreviewUrl,
  setStyleNotFoundMessage,
  setStyleType,
  updateScene,
  setMockupLogos,
  clearMockupLogos,
} = appSlice.actions;

export const appReducer = appSlice.reducer;
